import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../store/slices/uiSlice';
import apiService from '../services/api.service';
import {
    Box, Paper, Typography, TextField, Button, Grid, Table, TableBody,
    TableCell, TableHead, TableRow, Divider, Alert,
    InputAdornment, CircularProgress, Autocomplete, TableContainer
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon } from '@mui/icons-material';
import ProjectChooseDialog from '../components/subcontract/ProjectChooseDialog';
import { SubcontractProjectOption, useSubcontractProjects } from '../hooks/useSubcontractProjects';

interface SubcontractSummary {
    subcontractid: number;
    subcontractnumber: string;
    vendorname: string;
    subcontractvalue: number;
    contractstartdate: string;
    contractcompletiondate: string;
    projectcode: string;
    status: string;
}

interface Variation {
    variationid: number;
    variationnumber: string;
    description: string;
    amount: number;
    approveddate: string | null;
    amendedcompletiondate: string | null;
    status: string;
    remarks: string | null;
    createddate: string;
}

const SubcontractVariation: React.FC = () => {
    const dispatch = useDispatch();
    const { companyId: selectedCompanyId, projects, loading: loadingProjects } = useSubcontractProjects();

    // Search / select state
    const [selectedProject, setSelectedProject] = useState<SubcontractProjectOption | null>(null);
    const [projectPickerOpen, setProjectPickerOpen] = useState(false);
    const [subcontracts, setSubcontracts] = useState<SubcontractSummary[]>([]);
    const [selectedSubcontract, setSelectedSubcontract] = useState<SubcontractSummary | null>(null);
    const [loadingSC, setLoadingSC] = useState(false);

    // Loaded data
    const [scSummary, setScSummary] = useState<SubcontractSummary | null>(null);
    const [variations, setVariations] = useState<Variation[]>([]);
    const [loadingData, setLoadingData] = useState(false);

    // New variation form
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState<string>('');
    const [approvedDate, setApprovedDate] = useState('');
    const [amendedDate, setAmendedDate] = useState('');
    const [saving, setSaving] = useState(false);

    // When project changes, load subcontracts for that project
    useEffect(() => {
        if (!selectedProject) {
            setSubcontracts([]);
            setSelectedSubcontract(null);
            return;
        }
        const loadSC = async () => {
            setLoadingSC(true);
            try {
                const params: any = { projectCode: selectedProject.code };
                if (selectedCompanyId) params.companyId = selectedCompanyId;
                const res = await apiService.listSubcontracts(params);
                const list = res?.data || res || [];
                setSubcontracts(Array.isArray(list) ? list : []);
            } catch (err: any) {
                dispatch(showSnackbar({ message: 'Failed to load subcontracts', severity: 'error' }));
            } finally {
                setLoadingSC(false);
            }
        };
        loadSC();
    }, [selectedProject, selectedCompanyId, dispatch]);

    // Load subcontract detail + variations
    const handleLoad = useCallback(async () => {
        if (!selectedSubcontract) {
            dispatch(showSnackbar({ message: 'Please select a subcontract', severity: 'warning' }));
            return;
        }
        setLoadingData(true);
        try {
            const [scRes, varRes] = await Promise.all([
                apiService.getSubcontract(selectedSubcontract.subcontractid),
                apiService.getVariations(selectedSubcontract.subcontractid),
            ]);
            const sc = scRes?.data || scRes;
            setScSummary(sc);
            const vars = varRes?.data || varRes || [];
            setVariations(Array.isArray(vars) ? vars : []);
        } catch (err: any) {
            dispatch(showSnackbar({ message: err.message || 'Failed to load subcontract data', severity: 'error' }));
        } finally {
            setLoadingData(false);
        }
    }, [selectedSubcontract, dispatch]);

    // Total variation amounts
    const totalVariations = variations.reduce((sum, v) => sum + Number(v.amount || 0), 0);

    // Save new variation
    const handleSave = async () => {
        if (!scSummary) return;
        if (!description.trim()) {
            dispatch(showSnackbar({ message: 'Description is required', severity: 'warning' }));
            return;
        }
        const numAmount = parseFloat(amount);
        if (isNaN(numAmount)) {
            dispatch(showSnackbar({ message: 'Please enter a valid amount', severity: 'warning' }));
            return;
        }

        setSaving(true);
        try {
            const payload: any = {
                description: description.trim(),
                amount: numAmount,
            };
            if (approvedDate) payload.approveddate = approvedDate;
            if (amendedDate) payload.amendedcompletiondate = amendedDate;

            await apiService.createVariation(scSummary.subcontractid, payload);
            dispatch(showSnackbar({ message: 'Variation saved successfully', severity: 'success' }));

            // Reset form
            setDescription('');
            setAmount('');
            setApprovedDate('');
            setAmendedDate('');

            // Reload variations
            const varRes = await apiService.getVariations(scSummary.subcontractid);
            const vars = varRes?.data || varRes || [];
            setVariations(Array.isArray(vars) ? vars : []);
        } catch (err: any) {
            dispatch(showSnackbar({ message: err.message || 'Failed to save variation', severity: 'error' }));
        } finally {
            setSaving(false);
        }
    };

    const formatNumber = (val: number) =>
        val.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleDateString('en-GB');
        } catch {
            return dateStr;
        }
    };

    return (
        <Box component="section" aria-labelledby="sv-heading" sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography
                component="h1"
                id="sv-heading"
                variant="h6"
                sx={{ mb: 2, fontWeight: 600, fontSize: { xs: 18, sm: 20 } }}
            >
                Subcontract Variations
            </Typography>

            {/* Search / Select Section */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Select Subcontract
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField
                            size="small"
                            label="Project Number"
                            value={selectedProject ? `${selectedProject.code} - ${selectedProject.name}` : ''}
                            onClick={() => setProjectPickerOpen(true)}
                            placeholder="Choose project..."
                            InputProps={{
                                readOnly: true,
                                endAdornment: loadingProjects
                                    ? <CircularProgress size={18} />
                                    : <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Autocomplete
                            size="small"
                            options={subcontracts}
                            loading={loadingSC}
                            getOptionLabel={(opt) => `${opt.subcontractnumber} - ${opt.vendorname || ''}`}
                            value={selectedSubcontract}
                            onChange={(_e, val) => {
                                setSelectedSubcontract(val);
                                setScSummary(null);
                                setVariations([]);
                            }}
                            isOptionEqualToValue={(opt, val) => opt.subcontractid === val.subcontractid}
                            disabled={!selectedProject}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Subcontract Number"
                                    placeholder="Search subcontract..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingSC && <CircularProgress size={18} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={loadingData ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                            onClick={handleLoad}
                            disabled={!selectedSubcontract || loadingData}
                            sx={{ bgcolor: '#F0AB00', '&:hover': { bgcolor: '#F2BB2E' }, color: '#000' }}
                        >
                            Load
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Subcontract Summary */}
            {scSummary && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                        Subcontract Summary
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                size="small"
                                label="SC Number"
                                value={scSummary.subcontractnumber || ''}
                                InputProps={{ readOnly: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                size="small"
                                label="Vendor"
                                value={scSummary.vendorname || ''}
                                InputProps={{ readOnly: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                size="small"
                                label="Original Value (KWD)"
                                value={formatNumber(Number(scSummary.subcontractvalue || 0))}
                                InputProps={{ readOnly: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                size="small"
                                label="Start Date"
                                value={formatDate(scSummary.contractstartdate)}
                                InputProps={{ readOnly: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                size="small"
                                label="End Date"
                                value={formatDate(scSummary.contractcompletiondate)}
                                InputProps={{ readOnly: true }}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Existing Variations Table */}
            {scSummary && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                        Existing Variations
                    </Typography>
                    {variations.length === 0 ? (
                        <Alert severity="info" sx={{ mb: 1 }}>No variations recorded for this subcontract.</Alert>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>Variation No</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">Amount (KWD)</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Approved Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Amended Completion Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {variations.map((v) => (
                                        <TableRow key={v.variationid} hover>
                                            <TableCell>{v.variationnumber}</TableCell>
                                            <TableCell>{v.description}</TableCell>
                                            <TableCell align="right" sx={{ color: Number(v.amount) < 0 ? 'error.main' : 'inherit' }}>
                                                {formatNumber(Number(v.amount))}
                                            </TableCell>
                                            <TableCell>{formatDate(v.approveddate)}</TableCell>
                                            <TableCell>{formatDate(v.amendedcompletiondate)}</TableCell>
                                            <TableCell>{v.status}</TableCell>
                                        </TableRow>
                                    ))}
                                    {/* Total Row */}
                                    <TableRow sx={{ backgroundColor: '#fafafa' }}>
                                        <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total Variations</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {formatNumber(totalVariations)}
                                        </TableCell>
                                        <TableCell colSpan={3} />
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            )}

            {/* Create New Variation Form */}
            {scSummary && (
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                        Create New Variation
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                size="small"
                                label="Description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                multiline
                                minRows={3}
                                maxRows={6}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                size="small"
                                label="Variation Amount (KWD)"
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">KWD</InputAdornment>,
                                }}
                                inputProps={{ step: '0.001' }}
                                helperText="Can be positive (addition) or negative (deduction)"
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                size="small"
                                label="Approved Date"
                                type="date"
                                value={approvedDate}
                                onChange={(e) => setApprovedDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                helperText="Optional"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                size="small"
                                label="Amended Completion Date"
                                type="date"
                                value={amendedDate}
                                onChange={(e) => setAmendedDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                helperText="Optional — overrides the original contract completion date"
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                            onClick={handleSave}
                            disabled={saving || !description.trim() || !amount}
                            sx={{ bgcolor: '#F0AB00', '&:hover': { bgcolor: '#F2BB2E' }, color: '#000' }}
                        >
                            Save Variation
                        </Button>
                    </Box>
                </Paper>
            )}
            <ProjectChooseDialog
                open={projectPickerOpen}
                projects={projects}
                loading={loadingProjects}
                selectedCode={selectedProject?.code}
                onClose={() => setProjectPickerOpen(false)}
                onSelect={(project) => {
                    setSelectedProject(project);
                    setSelectedSubcontract(null);
                    setScSummary(null);
                    setVariations([]);
                }}
            />
        </Box>
    );
};

export default SubcontractVariation;
